import { useState } from "react"
import { Slider } from "@/components/ui/slider"
import { useToast } from "@/components/ui/use-toast"
import { PrimaryActionRow } from "../pipeline/StagePrimitives"
import { RunSummaryView } from "./RunSummaryView"
import { digestRunSummary, apiErrorMessage } from "../../lib/program"
import type { ProgramStatus, RunSummary } from "../../types/outreach-program.types"

interface Props {
  /** "consent" | "check-in": used in labels. */
  noun: string
  eligible: number
  readyLabel: string
  emptyLabel: string
  helpText: string
  program: ProgramStatus | null
  run: (limit: number) => Promise<RunSummary>
  onDone: () => void
}

/**
 * "Start N <noun> calls": one run of the clinic's program controller for N
 * patients (same classification, caps and booking as the 10-minute tick).
 * With the program disabled this is a one-off manual run (D11).
 */
export function ManualRunAction({ noun, eligible, readyLabel, emptyLabel, helpText, program, run, onDone }: Props) {
  const { toast } = useToast()
  const [limit, setLimit] = useState(10)
  const [running, setRunning] = useState(false)
  const [last, setLast] = useState<RunSummary | null>(null)

  const n = Math.min(limit, eligible)
  const manual = program?.status === "disabled"
  const planOnly = program?.status === "paused" || (program?.status === "enabled" && program.mode === "shadow")
  const what = `${noun[0].toUpperCase()}${noun.slice(1)} calls`

  const start = async () => {
    setRunning(true)
    try {
      const summary = await run(n)
      setLast(summary)
      const d = digestRunSummary(summary, what)
      toast({ title: d.title, description: d.description, variant: d.errors ? "destructive" : "default" })
      onDone()
    } catch (err: any) {
      toast({
        title: `${what}: not started`,
        description: apiErrorMessage(err, "The run failed"),
        variant: "destructive",
      })
    } finally {
      setRunning(false)
    }
  }

  const modeNote = manual
    ? " The clinic program is disabled, so this is a one-off manual run through the same caps."
    : planOnly
      ? ` The program is ${program?.status === "paused" ? "paused" : "in shadow mode"}: the run records a plan and books nothing.`
      : ""

  return (
    <PrimaryActionRow
      label={eligible > 0 ? readyLabel : emptyLabel}
      count={eligible}
      helpText={helpText + modeNote}
      buttonLabel={
        eligible === 0 ? "Nothing to do" : `Start ${n} ${noun} calls${manual ? " (manual run)" : ""}`
      }
      onClick={start}
      loading={running}
      disabled={eligible === 0}
    >
      {eligible > 0 && (
        <div className="space-y-3 pt-2 border-t">
          <div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Patient limit this run</span>
              <span className="font-semibold text-primary tabular-nums bg-primary/10 rounded px-1.5 py-0.5">
                {n}
              </span>
            </div>
            <Slider
              value={[n]}
              onValueChange={([v]) => setLimit(v)}
              min={1}
              max={Math.min(500, eligible)}
              step={1}
              className="mt-2 [&_[role=slider]]:bg-primary [&_[role=slider]]:border-primary [&_[data-orientation=horizontal]>[data-orientation=horizontal]]:bg-primary"
            />
          </div>
        </div>
      )}
      {last && <RunSummaryView summary={last} what={`Last run: ${what.toLowerCase()}`} />}
    </PrimaryActionRow>
  )
}
